import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { UserResponseModel } from '../../../models/users/UserResponseModel';
import { IHttpService, HttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import {
	IFactMarketShareDataPreviousQuartersRequestModel,
	IFactMarketShareDataSearchRequestModel
} from '../../../models/factMarketShareDatas/FactMarketShareDataSearchRequestModel';
import { IFactMarketShareDataPreviousQuartersResponseModel } from '../../../models/factMarketShareDatas/FactMarketShareDataPreviousQuarterResponseModel';
import { FactMarketShareDataSearchResponseModel } from '../../../models/factMarketShareDatas/FactMarketShareDataSearchResponseModel';
import { ProductDetailsMarketShareListingResponseModel } from '../../../models/factMarketShareDatas/ProductDetailsMarketShareListingResponseModel';
import { Control, useForm, useWatch } from 'react-hook-form';
import { IManualInputFiltersData } from '../models/manualInputFilters';
import { IManualInputTableData } from '../models/manualInputTableData';

export const useManualInput = (
	filtersDataControl: Control<IManualInputFiltersData, any>
) => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const { control, setValue, watch } = useForm<IManualInputTableData>({
		defaultValues: {
			expandedRows: {},
			tableMainData: [],
			tablePreviousQuartersData: {
				quarterData: []
			},
			userHasLeadingBrandRights: false,
			hasSearchResults: false,
			loadingMainData: false,
			loadingPreviousQuartersData: true,
			showPreviousQuarter: false,
			sortField: '',
			sortOrder: undefined
		}
	});

	const showPreviousQuarter = watch('showPreviousQuarter');

	const {
		yearFilter,
		quarterFilter,
		divisionFilter,
		countryFilter,
		brandFilter,
		selectedProductCategoriesFilter,
		selectedCompetitorsFilter
	} = useWatch<IManualInputFiltersData>({
		control: filtersDataControl,
		defaultValue: {
			yearFilter: new Date(),
			quarterFilter: 0,
			divisionFilter: 0,
			countryFilter: 0,
			brandFilter: 0,
			selectedProductCategoriesFilter: [],
			selectedCompetitorsFilter: []
		}
	});

	function getProductsRequestModel(): IFactMarketShareDataSearchRequestModel | null {
		if (
			!yearFilter ||
			!quarterFilter ||
			!divisionFilter ||
			!countryFilter ||
			!brandFilter ||
			!selectedProductCategoriesFilter ||
			!selectedCompetitorsFilter
		) {
			return null;
		}

		const competitorIds =
		selectedCompetitorsFilter.map(
				(s) => s.competitorId as number
			) ?? [];

		return {
			year: yearFilter.getFullYear(),
			quarter: quarterFilter,
			countryId: Number(countryFilter),
			brandId: Number(brandFilter),
			divisionId: Number(divisionFilter),
			productDetailIds: selectedProductCategoriesFilter,
			competitorBrandIds: competitorIds
		};
	}

	const fetchProducts = async (
		updateTable = true,
		silentUpdate = false
	): Promise<ProductDetailsMarketShareListingResponseModel[]> => {
		const requestData = getProductsRequestModel();

		if (!requestData) {
			return [];
		}

		try {
			if (silentUpdate === false) {
				updateTable && setValue('loadingMainData', true);
			}

			const productsResponse =
				await httpService.postGeneric<FactMarketShareDataSearchResponseModel>(
					'factMarketShareDatas/search',
					JSON.stringify(requestData),
					undefined,
					false
				);

			const products = productsResponse.productDetails;

			if (updateTable) {
				if (silentUpdate === false) {
					const expandedCategories = products
						.map((p) => p.productCategoryDescription)
						.filter(
							(value, index, arr) => arr.indexOf(value) === index
						)
						.reduce((map, obj) => {
							(map as any)[obj] = true;
							return map;
						}, {});

					setValue('expandedRows', expandedCategories);
				}

				setValue(
					'userHasLeadingBrandRights',
					productsResponse.hasLeadingBrandRights
				);
				setValue('tableMainData', products);

				setValue('hasSearchResults', products.length > 0);

				showPreviousQuarter && fetchPreviousQuarterData();

				setValue('loadingMainData', false);
			}

			return products;
		} catch (err) {
			console.error(err);
			notifierService.error();

			return [];
		}
	};

	function getPreviousQuarterDataRequestModel(): IFactMarketShareDataPreviousQuartersRequestModel | null {
		if (
			!yearFilter ||
			!quarterFilter ||
			!divisionFilter ||
			!countryFilter ||
			!brandFilter ||
			!selectedProductCategoriesFilter ||
			!selectedCompetitorsFilter
		) {
			return null;
		}

		return {
			year: yearFilter.getFullYear(),
			quarter: quarterFilter,
			countryId: Number(countryFilter),
			brandId: Number(brandFilter),
			divisionId: Number(divisionFilter),
			productDetailIds: selectedProductCategoriesFilter,
			previousQuartersCount: 4
		};
	}

	const fetchPreviousQuarterData = async () => {
		const requestData = getPreviousQuarterDataRequestModel();

		setValue('loadingPreviousQuartersData', true);

		const prevQuarterProducts =
			await httpService.postGeneric<IFactMarketShareDataPreviousQuartersResponseModel>(
				'factMarketShareDatas/summary',
				JSON.stringify(requestData),
				undefined,
				false
			);

		setValue('tablePreviousQuartersData', prevQuarterProducts);
		setValue('loadingPreviousQuartersData', false);
	};

	return {
		control,
		setValue,
		fetchProducts,
		fetchPreviousQuarterData
	};
};
